import { SdkContext } from '../internal'

export type MediaPurpose = 'contentVideo' | 'eventRecording' | 'galleryImage' | 'classifiedImage' | 'parcelPhoto' | 'supportPhoto'
  | 'statusVideo' | 'contentCommentVideo' | 'statusCommentVideo' | 'videoPoster' | 'lessonVideo' | 'upvoteImage'
  | 'channelImage' | 'channelVideo'
  /** F-155: the community's welcome video (owner and admin, no space). */
  | 'welcomeVideo'
  /** F-178: an episode's audio (MP3, M4A, AAC), video and cover, a show's cover, a Drop on an episode comment. */
  | 'episodeAudio' | 'episodeVideo' | 'episodeCover' | 'showCover' | 'mediaEpisodeCommentVideo'
  /** F-178 1b: "Podcast" and "Vídeo" of the Feed's "Novo Status". */
  | 'feedAudio' | 'feedVideo'

/** F-107: where a Drop (a video recorded in the browser) can go. */
export type DropPurpose = 'contentVideo' | 'statusVideo' | 'contentCommentVideo' | 'statusCommentVideo' | 'lessonVideo' | 'channelVideo' | 'mediaEpisodeCommentVideo'

export interface MediaUploadTicket {
  uploadUrl: string
  method: string
  headers: Record<string, string>
  key: string
  name: string
  purpose: MediaPurpose
  expiresAt: string
  maxBytes: number
}

export interface MediaAsset {
  id: string
  purpose: MediaPurpose
  kind: 'video' | 'image' | 'audio'
  spaceId: string
  name: string
  mimeType: string
  size: number
  attached: boolean
  hasThumbnail: boolean
  createdAt: string
}

/**
 * F-34 `video.transcodeMinutesPerMonth`, team only: the video was accepted but keeps
 * its original file (no adaptive version), with the API's translated `message`.
 */
export interface TranscodeLimit {
  code: 'transcodeQuotaReached' | 'transcodeNotOnPlan' | 'transcodeTrialSpent' | 'streamingUnavailable'
  used?: number
  limit?: number
  /** The one-time native video gift granted (0 when none). */
  trialMinutes?: number
  message?: string
}

export interface MediaConfirmResponse {
  asset: MediaAsset
  usage?: Record<string, unknown>
  warning: 'nearLimit' | 'overLimit' | null
  transcodeLimit?: TranscodeLimit
}

export interface SignedMediaUrl {
  id: string
  url?: string
  /** A video's cover: the one its uploader chose, else the automatic frame (a few seconds after confirm). */
  thumbnailUrl?: string
  /** Only with a chosen cover: the whole image and its id. */
  posterUrl?: string
  posterMediaId?: string
  expiresAt?: string
  kind?: 'video' | 'image'
  mimeType?: string
  name?: string
  size?: number
  /** Adaptive conversion (native streaming); `null` when it does not apply and the MP4 plays as always. */
  processingStatus?: 'queued' | 'processing' | 'ready' | 'failed' | null
  /** Only when the HLS version is ready: signed query `params` go on every request to the manifest, playlists and segments. */
  hls?: {
    manifestUrl: string
    params: Record<string, string>
    expiresAt: string
    heights: number[]
  } | null
  /** A video's length, read by the server once the file is processed. */
  durationSeconds?: number | null
  /** Only for who may choose the cover: frames taken from the video (empty while they are being made). */
  coverCandidates?: Array<{ index: number; url: string }>
  /** The chosen frame, `null` when none (the automatic one shows). */
  coverCandidateIndex?: number | null
  /** Present instead of `url` when the person cannot open it. */
  error?: 'forbidden' | 'notFound' | 'removed'
}

/** Where a library file is used: where it was sent (`primary`) or a reuse. */
export type MediaUseType = 'status' | 'content' | 'courseLesson' | 'mediaEpisode' | 'event' | 'contentComment' | 'statusComment' | 'mediaEpisodeComment' | 'communityWelcome'

export interface MediaLibraryUse {
  type: MediaUseType
  id: string
  primary: boolean
  title: string | null
  spaceId?: string | null
  /** Lesson: its course; episode: its show; comment: its post or episode. */
  parentId?: string
  parentTitle?: string | null
}

/** F-178 1b: an audio or video of the community's Media library. Never carries a link. */
export interface MediaLibraryItem {
  id: string
  kind: 'audio' | 'video'
  purpose: MediaPurpose
  name: string
  mimeType: string
  size: number
  durationSeconds: number | null
  processingStatus: 'queued' | 'processing' | 'ready' | 'failed' | null
  hasThumbnail: boolean
  createdAt: string
  uploader: { id: string; name: string; avatar?: string | null } | null
  /** "Used in N places": the original owner, if it still exists, plus every reuse. */
  usedIn: number
  source: { type: MediaUseType; id: string; title: string | null } | null
  canDelete: boolean
  /** The file sits in paid content: reusing it elsewhere (owner and admin only) opens it. */
  paidOrigin?: boolean
  /** Where the file can be reused (`episode`, `status`, `content`, `lesson`); a paid-origin file only in `episode`. */
  reusableIn?: LibraryTarget[]
}

/** F-178 1b: where a library file is about to be reused. */
export type LibraryTarget = 'status' | 'episode' | 'lesson' | 'content'

export interface MediaLibraryPage {
  items: MediaLibraryItem[]
  pagination: { page: number; limit?: number; total: number; totalPages: number } | null
}

/** One file of `upload-urls`: a signed `PUT`, or why that file was refused (the others go on). */
export type MediaBatchTicket =
  | ({ index: number; error?: undefined } & Omit<MediaUploadTicket, 'purpose'>)
  | { index: number; name?: string; error: { code: string; message?: string; params?: Record<string, unknown> } }

/** One item of `confirm-batch`: the asset (a gallery image comes back `processingStatus: queued`), or its error. */
export type MediaBatchConfirmation =
  | { index: number; asset: MediaAsset & { processingStatus?: string | null }; error?: undefined; transcodeLimit?: TranscodeLimit }
  | { index: number; asset?: undefined; error: { code: string; message?: string } }

/** F-76: privately hosted media. The app never receives a storage key or a public URL, only signed links. */
export class MediaResource {
  constructor(private ctx: SdkContext) {}

  /** Step 1: a signed `PUT` bound to the exact type and size. Never log it. */
  /** `spaceId` is required for every purpose but `welcomeVideo` (F-155), which lives in no space. */
  async requestUpload(body: { purpose: MediaPurpose; spaceId?: string; name: string; mimeType: string; size: number }): Promise<MediaUploadTicket> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media/upload-url', hasBody: true }, { body })
  }

  /** Step 3: the API checks the object and creates the asset. */
  async confirm(body: { key: string; name: string; spaceId?: string; size?: number }): Promise<MediaConfirmResponse> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media/confirm', hasBody: true }, { body })
  }

  /** Bulk step 1: up to 50 signed `PUT`s (10 minutes each) of one purpose and space; each file is checked on its own. */
  async requestUploads(body: { purpose: MediaPurpose; spaceId: string; files: Array<{ name: string; mimeType: string; size: number }> }): Promise<{ items: MediaBatchTicket[]; warning?: string | null }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media/upload-urls', hasBody: true }, { body })
  }

  /** Bulk step 3: confirms up to 50 uploads of one space; idempotent per `key`. */
  async confirmBatch(body: { spaceId: string; items: Array<{ key: string; name: string; size?: number }> }): Promise<{ items: MediaBatchConfirmation[] }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media/confirm-batch', hasBody: true }, { body })
  }

  /** A one-hour signed link, after the access check. */
  async getUrl(id: string): Promise<SignedMediaUrl> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media/{id}/url' }, { id })
  }

  /** Up to 100 ids at once (a gallery page). */
  async getUrls(ids: string[]): Promise<SignedMediaUrl[]> {
    const result = (await this.ctx.call({ httpMethod: 'POST', path: '/media/urls', hasBody: true }, { body: { ids } })) as SignedMediaUrl[] | { items?: SignedMediaUrl[] }
    return Array.isArray(result) ? result : result?.items || []
  }

  /** Team of the owning community or SuperAdmin: puts a video whose conversion `failed` back in the queue (202). 409 not failed, 429 limit, 503 unavailable. */
  async reprocess(id: string): Promise<{ id: string; processingStatus: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media/{id}/reprocess' }, { id })
  }

  /**
   * F-107: the cover of a video, by its uploader only: a confirmed `videoPoster` image of the same space,
   * or `null` to go back to the automatic frame. The previous chosen cover is deleted by the API.
   */
  async setPoster(videoId: string, posterMediaId: string | null): Promise<{ id: string; posterMediaId: string | null }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/media/{id}/poster', hasBody: true }, { id: videoId, body: { posterMediaId } })
  }

  /** Picks one of `coverCandidates` as the video's cover. 409 while the frames are not ready, 400 for a bad index. */
  async setCover(id: string, candidate: number): Promise<{ id: string; coverCandidateIndex: number }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/media/{id}/cover', hasBody: true }, { id, body: { candidate } })
  }

  /** F-178 1b: the Media library (team: every audio and video; others: their own). */
  /** `for`: where the file will go, so the API lists only what that place can take. */
  async library(query: { kind?: 'audio' | 'video'; q?: string; page?: number; limit?: number; for?: LibraryTarget } = {}): Promise<MediaLibraryPage> {
    const clean = Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    const body = await this.ctx.envelope<{ data?: MediaLibraryItem[]; pagination?: MediaLibraryPage['pagination'] }>(
      { httpMethod: 'GET', path: '/media/library' }, { query: clean }
    )
    return { items: body?.data ?? [], pagination: body?.pagination ?? null }
  }

  /** One library file and where it is used (410 once deleted from the library). */
  async libraryItem(id: string): Promise<MediaLibraryItem & { uses: MediaLibraryUse[] }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media/library/{id}' }, { id })
  }

  /** Owner and admin: deletes the file from every use, which then shows "mídia removida". */
  async libraryRemove(id: string): Promise<{ id: string; removedUses: MediaLibraryUse[] }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/media/library/{id}' }, { id })
  }

  /** Only for an upload that was never attached to anything. */
  async delete(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/media/{id}' }, { id })
  }
}
